// Component: src/theme/snippets/search-input.liquid

import { EVENT_RELOAD_SAVED_SCHOOLS, SAVED_SCHOOL_KEY, SavedSchoolsModal } from "@app/components/saved-schools-modal";
import { searchCollections, searchProducts } from "@app/lib/searchApi";
import { debounce } from "@app/utils/debounce";

export const EVENT_RELOAD_HISTORY_LIST = "reload-history-list";

class SearchInput extends HTMLElement {
  $input!: HTMLInputElement;
  $results!: HTMLElement;
  $history!: HTMLElement;

  isAnimating = false;
  searchCounter = 0;
  isB2B = false;

  constructor() {
    super();
  }

  connectedCallback() {
    this.$input = this.querySelector("input")!;
    this.$results = this.querySelector(".search-results")!;
    this.$history = this.querySelector(".search-history")!;

    // detect B2B mode from window flag set in theme.liquid
    this.isB2B = !!(window as any).isB2B;

    // B2B: hide history (saved schools) — not relevant
    if (this.isB2B) {
      this.$history.classList.add("!hidden");
    } else {
      this.handleHistoryResults();
    }

    const handleSearchDebounced = debounce(this.handleSearch.bind(this), 300);

    this.$input.addEventListener("focus", () => {
      this.handleOverlayCreate();
      this.classList.add("focus");
      if (this.$input.value.length) {
        this.classList.add("active");
      }
    });
    this.$input.addEventListener("blur", (e) => {
      this.handleOverlayRemove();
      setTimeout(() => {
        this.classList.remove("focus");
        if (!this.isAnimating) {
          this.classList.remove("active");
        }
      }, 100);
    });
    this.$input.addEventListener("input", (e) => {
      const value = e.target?.value || "";
      handleSearchDebounced(value);
    });

    if (!this.isB2B) {
      document.addEventListener(EVENT_RELOAD_HISTORY_LIST, () => {
        this.handleHistoryResults();
      });
    }
  }

  async handleSearch(query: string) {
    this.$results.innerHTML = "";
    this.classList.remove("active");

    if (query.length > 0) {
      this.searchCounter++;
      const currentCount = this.searchCounter;

      if (this.isB2B) {
        // B2B: search products from their catalog (Shopify auto-filters)
        searchProducts(query).then((products) => {
          if (currentCount === this.searchCounter) {
            this.displayProductResults(products || []);
            if (products?.length) this.classList.add("has-results");
          }
        });
      } else {
        // B2C: search collections (schools)
        searchCollections(query).then((collections) => {
          if (currentCount === this.searchCounter) {
            this.displayResults(collections || []);
            if (collections?.length) this.classList.add("has-results");
          }
        });
      }
    } else {
      this.classList.remove("has-results");
    }
  }

  displayResults(collections: any[]) {
    if (collections.length > 0) {
      this.classList.add("active");
    }

    collections.forEach((collection) => {
      const $item = document.createElement("button");
      $item.classList.add("search-result-item");
      $item.addEventListener("click", (e) => {
        this.animateIcon($item.querySelector(".item-image img")!, () => {
          this.saveSchool(collection);
          window.location.href = collection.url;
        });
      });

      const $image = document.createElement("div");
      $image.classList.add("item-image");
      $image.innerHTML = `<img src="${collection.featured_image.url}" alt="${collection.title}" />`;
      $item.appendChild($image);

      const $text = document.createElement("div");
      $text.classList.add("item-title");
      $text.innerHTML = collection.title;
      $item.appendChild($text);

      this.$results.appendChild($item);
    });
  }

  displayProductResults(products: any[]) {
    if (products.length > 0) {
      this.classList.add("active");
    }

    products.forEach((product) => {
      const $item = document.createElement("a");
      $item.classList.add("search-result-item");
      $item.href = product.url;

      const $image = document.createElement("div");
      $image.classList.add("item-image");
      const imageUrl = product.featured_image?.url || product.image || "";
      if (imageUrl) {
        $image.innerHTML = `<img src="${imageUrl}" alt="${product.title}" />`;
      }
      $item.appendChild($image);

      const $text = document.createElement("div");
      $text.classList.add("item-title");
      $text.innerHTML = product.title;
      $item.appendChild($text);

      this.$results.appendChild($item);
    });
  }

  handleHistoryResults() {
    let collections: any = localStorage.getItem(SAVED_SCHOOL_KEY) || "[]";
    collections = JSON.parse(collections);

    this.$history.innerHTML = "";

    if (!collections.length) {
      this.$history.classList.add("!hidden");
      return;
    }

    collections.forEach((collection: any) => {
      const $item = document.createElement("a");
      $item.classList.add("search-result-item");
      $item.href = collection.url;

      const $icon = document.createElement("div");
      $icon.classList.add("item-image");
      $icon.innerHTML = window.iconClockLapseXxs;
      $item.appendChild($icon);

      const $text = document.createElement("div");
      $text.classList.add("item-title");
      $text.innerHTML = collection.title;
      $item.appendChild($text);

      const $close = document.createElement("button");
      $close.classList.add("item-close");
      $close.innerHTML = window.iconCloseXs;
      $item.appendChild($close);
      $close.addEventListener("click", (e) => {
        e.preventDefault();

        // refocus input if history is not empty
        if (collections.length > 1) {
          setTimeout(() => {
            this.$input.focus();
          }, 110);
        }

        this.removeSchool(collection);
        document.dispatchEvent(new Event(EVENT_RELOAD_HISTORY_LIST));
        document.dispatchEvent(new Event(EVENT_RELOAD_SAVED_SCHOOLS));
      });

      this.$history.appendChild($item);
    });

    this.$history.classList.remove("!hidden");
  }

  animateIcon($icon: HTMLElement, callback?: () => void) {
    this.isAnimating = true;

    // make a copy of the icon and place on top of the original with a fixed position
    var rect = $icon.getBoundingClientRect();
    const $copy = $icon.cloneNode(true) as HTMLElement;
    $copy.classList.add("school-icon-animate");
    $copy.style.top = `${rect.top}px`;
    $copy.style.left = `${rect.left}px`;
    document.body.appendChild($copy);

    // animate the copied icon towards the bookmark icon
    const $bookmark = document.getElementById("saved-schools-button");
    if ($bookmark) {
      const bookmarkRect = $bookmark.getClientRects()[0];
      setTimeout(() => {
        $copy.style.top = `${bookmarkRect.top + 8}px`;
        $copy.style.left = `${bookmarkRect.left + 6}px`;
        $copy.style.transform = "scale(0.5)";
        $copy.style.opacity = "0.1";
        $copy.style.zIndex = "200";
      }, 1);

      // animate bookmark icon
      setTimeout(() => {
        $bookmark.style.transform = "scale(1.2)";
        setTimeout(() => {
          $bookmark.style.transform = "scale(1)";
        }, 250);
      }, 700);

      // remove the copy icon after the animation is complete
      setTimeout(() => {
        $copy.remove();
        callback?.();
      }, 1000);
    }
  }

  saveSchool(school: any) {
    SavedSchoolsModal.prototype.saveSchool.call(this, school);
  }

  removeSchool(school: any) {
    SavedSchoolsModal.prototype.removeSchool.call(this, school);
  }

  handleOverlayCreate() {
    if (this.classList.contains("no-overlay")) {
      return;
    }

    const $overlay = document.createElement("div");
    $overlay.classList.add("search-overlay");
    this.parentElement?.after($overlay);
    setTimeout(() => {
      $overlay.classList.add("show");
    }, 10);
  }

  handleOverlayRemove() {
    document.querySelector(".search-overlay")?.remove();
  }
}

customElements.define("search-input", SearchInput);
